import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TPayableInvoices,
	TPaymentQuote,
	TQuoteChangedDetails,
	TTryToPayResult,
} from 'services/api/payments/types';
import { TPayableRequest, TPaymentQuoteRequest, TPaymentsStore, TTryToPayRequest } from './types';

// ==========================================:
export const initialState: TPaymentsStore = {
	payable: null,
	payableLoading: false,
	quote: null,
	quoteLoading: false,
	quoteError: null,
	tryToPayLoading: false,
	tryToPayResult: null,
	tryToPayError: null,
	quoteChanged: null,
};

// ==========================================:
const payments = createSlice({
	name: '@@payments',
	initialState,
	reducers: {
		getPayableRequest: (state, _action: PayloadAction<TPayableRequest>) => {
			state.payableLoading = true;
		},
		getPayableSuccess: (state, action: PayloadAction<TPayableInvoices>) => {
			state.payable = action.payload;
			state.payableLoading = false;
		},
		getPayableFailure: (state) => {
			state.payableLoading = false;
		},

		getQuoteRequest: (state, _action: PayloadAction<TPaymentQuoteRequest>) => {
			// quoteChanged is kept: after 409 quote_changed the new quote is shown together with the notice
			state.quote = null;
			state.quoteError = null;
			state.quoteLoading = true;
		},
		getQuoteSuccess: (state, action: PayloadAction<TPaymentQuote>) => {
			state.quote = action.payload;
			state.quoteLoading = false;
		},
		getQuoteFailure: (state, action: PayloadAction<string>) => {
			state.quoteLoading = false;
			state.quoteError = action.payload;
		},
		clearQuote: (state) => {
			state.quote = null;
			state.quoteError = null;
			state.quoteLoading = false;
		},

		tryToPayRequest: (state, _action: PayloadAction<TTryToPayRequest>) => {
			state.tryToPayLoading = true;
			state.tryToPayResult = null;
			state.tryToPayError = null;
			state.quoteChanged = null;
		},
		tryToPayProgress: (state, action: PayloadAction<TTryToPayResult>) => {
			// "processing": the result is shown while the status is being polled
			state.tryToPayResult = action.payload;
		},
		tryToPaySuccess: (state, action: PayloadAction<TTryToPayResult>) => {
			state.tryToPayResult = action.payload;
			state.tryToPayLoading = false;
		},
		tryToPayFailure: (state, action: PayloadAction<string>) => {
			state.tryToPayLoading = false;
			state.tryToPayError = action.payload;
		},
		tryToPayQuoteChanged: (state, action: PayloadAction<TQuoteChangedDetails>) => {
			state.tryToPayLoading = false;
			state.quoteChanged = action.payload;
		},
		tryToPayQuoteExpired: (state) => {
			state.tryToPayLoading = false;
			state.quote = null;
		},

		resetPayment: (state) => {
			state.quote = null;
			state.quoteLoading = false;
			state.quoteError = null;
			state.tryToPayLoading = false;
			state.tryToPayResult = null;
			state.tryToPayError = null;
			state.quoteChanged = null;
		},

		paymentsInitState: () => initialState,
	},
});

export default payments.reducer;
export const {
	getPayableRequest,
	getPayableSuccess,
	getPayableFailure,
	getQuoteRequest,
	getQuoteSuccess,
	getQuoteFailure,
	clearQuote,
	tryToPayRequest,
	tryToPayProgress,
	tryToPaySuccess,
	tryToPayFailure,
	tryToPayQuoteChanged,
	tryToPayQuoteExpired,
	resetPayment,
	paymentsInitState,
} = payments.actions;
